import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { THEME_KEY, applyTheme } from "../theme";
import "./navbar.css";

export default function Navbar({ isMobile = false, onToggleSidebar, sidebarOpen = false }) {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const firstName = user?.name?.trim()?.split(/\s+/)?.[0] || "Rider";

  const unlockedFeatures = [
    { label: "Rides", path: "/rides" },
    { label: "Chat", path: "/chat" },
    { label: "SOS", path: "/sos" },
  ];

  const mechanicFeature = user?.role === "mechanic"
    ? [{ label: "My Garage", path: "/my-garage" }]
    : [];

  const navItems = [
    { label: "Home", path: "/" },
    { label: "Bikes", path: "/bikes" },
    { label: "Bike Prices", path: "/bike-prices" },
    { label: "Riders", path: "/riders" },
    { label: "Showrooms", path: "/showrooms" },
    { label: "Calculator", path: "/calculator" },
    { label: "Mechanic Finder", path: "/mechanic-finder" },
    ...mechanicFeature,
    { label: "AI Bike", path: "/recommend" },
    { label: "Blogs", path: "/blogs" },
    { label: "Shop", path: "/shop" },
    { label: "About", path: "/about" },
    { label: "Feedback", path: "/feedback" },
    { label: "Profile", path: "/profile" },
    { label: "Owner", path: "/showroom-owner", authOnly: true },
  ];

  const [themeMode, setThemeMode] = React.useState(() => localStorage.getItem(THEME_KEY) || "auto");
  const [activeTheme, setActiveTheme] = React.useState(() => applyTheme(localStorage.getItem(THEME_KEY) || "auto"));

  React.useEffect(() => {
    const syncTheme = () => {
      const mode = localStorage.getItem(THEME_KEY) || "auto";
      setThemeMode(mode);
      setActiveTheme(applyTheme(mode));
    };

    syncTheme();
    const interval = setInterval(syncTheme, 60 * 1000);
    window.addEventListener("storage", syncTheme);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", syncTheme);
    };
  }, []);

  const toggleTheme = () => {
    const nextMode =
      themeMode === "auto"
        ? activeTheme === "dark"
          ? "light"
          : "dark"
        : themeMode === "light"
          ? "dark"
          : "auto";

    localStorage.setItem(THEME_KEY, nextMode);
    setThemeMode(nextMode);
    setActiveTheme(applyTheme(nextMode));
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="navbar">
      <div className="navbar-top-row">
        <div className="navbar-brand">
          <div className="navbar-title-wrap">
            <span className="navbar-kicker">Ride smarter</span>
            <h2>MotoHub India</h2>
          </div>
          <div className="premium-links">
            {user
              ? unlockedFeatures.map((feature) => (
                  <NavLink key={feature.label} to={feature.path} className="premium-pill">
                    {feature.label}
                  </NavLink>
                ))
              : unlockedFeatures.map((feature) => (
                  <NavLink key={feature.label} to="/login" className="premium-pill premium-pill-locked">
                    {feature.label}
                  </NavLink>
                ))}
            {user?.role === "mechanic" ? (
              <NavLink to="/my-garage" className="premium-pill">
                My Garage
              </NavLink>
            ) : null}
          </div>
        </div>

        <div className="navbar-actions">
          {isMobile ? (
            <button
              type="button"
              className="mobile-menu-btn"
              onClick={onToggleSidebar}
              aria-expanded={sidebarOpen}
              aria-label="Toggle side menu"
            >
              {sidebarOpen ? "Close" : "Menu"}
            </button>
          ) : null}

          {user ? (
            <div className="user-menu">
              <span className="user-greeting">Hi, {firstName}</span>
              <button onClick={handleLogout} className="logout-btn">
                Logout
              </button>
            </div>
          ) : (
            <NavLink to="/login" className="login-link">
              Login
            </NavLink>
          )}

          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="theme-toggle-btn"
            title={`Theme mode: ${themeMode}`}
          >
            {activeTheme === "dark" ? "Moon" : "Sun"}
          </button>
        </div>
      </div>

      <nav className="nav-links" aria-label="Primary">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.authOnly && !user ? "/login" : item.path}
            end={item.path === "/"}
            className={({ isActive }) => (isActive ? "nav-link nav-link-active" : "nav-link")}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
